import {
  Badge,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';

import { isDefined, isEmpty, isNotDefined, isNotEmpty } from '@enterprise/core';
import { DefaultToolTip, NumberFormat } from '@enterprise/ui';

import type { DropTwentileCellProps } from './types/drop-twentile-cell.props';

import { useDropTwentileCellConfiguration } from './hooks/drop-twentile-cell-configuration.hook';

const DropTwentileCell = ({ context, data, ...props }: DropTwentileCellProps) => {
  const {
    displayLabels,
    handleClear,
    handleClose,
    handleOpen,
    handleOptionChange,
    handleSelectAll,
    isAllSelected,
    isOpen,
    localSelected,
    options,
  } = useDropTwentileCellConfiguration({ context, data });

  if (props.node.level === -1) {
    return 'Total';
  }

  return (
    <Flex
      align="center"
      h="full"
      w="full">
      {options.length === 0 && (
        <Box
          color={displayLabels ? 'gray.800' : 'gray.400'}
          flex={1}
          fontSize="sm"
          px={2}>
          Not scored
        </Box>
      )}
      {options.length > 0 && (
        <>
          <Box flex={1}>
            <Popover
              isLazy
              isOpen={isOpen}
              onClose={handleClose}
              placement="bottom-start">
              <PopoverTrigger>
                <Button
                  color={displayLabels ? 'gray.800' : 'gray.400'}
                  fontWeight={displayLabels ? 'semibold' : 'normal'}
                  justifyContent="flex-start"
                  onClick={handleOpen}
                  px={2}
                  size="xs"
                  variant="ghost"
                  w="full">
                  <DefaultToolTip
                    isDisabled={isNotDefined(displayLabels) || isEmpty(displayLabels)}
                    label={displayLabels}>
                    <Box
                      isTruncated
                      textAlign="left"
                      w="full">
                      {displayLabels || 'Select twentiles…'}
                    </Box>
                  </DefaultToolTip>
                </Button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="auto">
                  <PopoverBody
                    bg="gray.50"
                    p={3}>
                    <VStack spacing={2}>
                      <Button
                        colorScheme="hsp"
                        onClick={handleSelectAll}
                        size="xs"
                        variant="outline"
                        w="full">
                        {isAllSelected ? 'Deselect all' : 'Select all'}
                      </Button>
                      <SimpleGrid
                        columns={2}
                        spacing={2}>
                        {options.map(({ count, twentile }) => {
                          return (
                            <Flex
                              alignItems="center"
                              bg="white"
                              borderColor="gray.200"
                              borderWidth={1}
                              gap={3}
                              key={twentile}
                              p={2}
                              rounded="md"
                              w="full">
                              <Checkbox
                                isChecked={isNotDefined(twentile) ? false : localSelected.includes(twentile)}
                                onChange={(e) => {
                                  handleOptionChange(e.target.checked, twentile);
                                }}
                                size="md"
                                w="full">
                                Twentile {twentile}
                              </Checkbox>
                              <Badge
                                alignItems="center"
                                borderColor="gray.600"
                                borderWidth={1}
                                colorScheme="red"
                                display="flex"
                                justifyContent="center"
                                ml="auto"
                                rounded="full">
                                -<NumberFormat value={count} />
                              </Badge>
                            </Flex>
                          );
                        })}
                      </SimpleGrid>
                    </VStack>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          </Box>
          {isDefined(displayLabels) && isNotEmpty(displayLabels) && (
            <CloseButton
              flexShrink={0}
              ml="auto"
              onClick={handleClear}
              size="sm"
            />
          )}
        </>
      )}
    </Flex>
  );
};

export default DropTwentileCell;
